import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { usersAPI } from '../api';
import './UserManager.css'; 

const UsersManager = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await usersAPI.getAll();
      setUsers(data.users);
      setStats(data.stats);
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (window.confirm(`Are you sure you want to delete user: ${userName}?`)) {
      try {
        await usersAPI.delete(userId);
        toast.success('User deleted successfully');
        fetchUsers();
      } catch (error) {
        toast.error('Failed to delete user');
      }
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const data = await usersAPI.getById(userId);
      setSelectedUser(data.user);
      setShowModal(true);
    } catch (error) {
      toast.error('Failed to fetch user details');
    }
  };

  // Rest of the component remains the same...

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone?.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="users-manager">
      <div className="page-header">
        <h1>User Management</h1>
        <div className="stats-cards">
          <div className="stat-card">
            <h3>Total Users</h3>
            <p className="stat-number">{stats.total || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Verified Users</h3>
            <p className="stat-number">{stats.verified || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Google Sign-ins</h3>
            <p className="stat-number">{stats.withGoogle || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Enrolled Users</h3>
            <p className="stat-number">{stats.enrolled || 0}</p>
          </div>
        </div>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Joined</th>
              <th>Enrolled Courses</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user._id}>
                <td className="user-cell">
                  <div className="user-info">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="user-avatar" />
                    ) : (
                      <div className="user-avatar-placeholder">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="user-name">{user.name || 'N/A'}</span>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>{user.phone || 'Not provided'}</td>
                <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                <td>{user.enrolledCourses?.length || 0}</td>
                <td>
                  <span className={`status-badge ${user.isEmailVerified ? 'verified' : 'unverified'}`}>
                    {user.isEmailVerified ? 'Verified' : 'Unverified'}
                  </span>
                </td>
                <td className="actions-cell">
                  <button 
                    onClick={() => viewUserDetails(user._id)} 
                    className="action-btn view-btn"
                    title="View Details"
                  >
                    👁️
                  </button>
                  <button 
                    onClick={() => handleDeleteUser(user._id, user.name)} 
                    className="action-btn delete-btn"
                    title="Delete User"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredUsers.length === 0 && (
          <div className="no-data">No users found</div>
        )}
      </div>

      {/* User Details Modal */}
      {showModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>User Details</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-profile">
                {selectedUser.picture ? (
                  <img src={selectedUser.picture} alt={selectedUser.name} className="profile-pic-large" />
                ) : (
                  <div className="profile-placeholder-large">
                    {selectedUser.name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <h3>{selectedUser.name}</h3>
                <p className="user-email">{selectedUser.email}</p>
                {selectedUser.phone && <p className="user-phone">📱 {selectedUser.phone}</p>}
              </div>
              
              <div className="enrollments-section">
                <h4>Enrolled Courses ({selectedUser.enrolledCourses?.length || 0})</h4>
                {selectedUser.enrolledCourses?.length > 0 ? (
                  <div className="enrollments-list">
                    {selectedUser.enrolledCourses.map((enrollment, index) => (
                      <div key={index} className="enrollment-card">
                        <div className="enrollment-course">
                          <strong>{enrollment.courseId?.title || 'Unknown Course'}</strong>
                        </div>
                        <div className="enrollment-details">
                          <span>Enrolled: {new Date(enrollment.enrolledAt).toLocaleDateString()}</span>
                          <span className={`status-badge ${enrollment.status}`}>
                            {enrollment.status}
                          </span>
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ width: `${enrollment.progress || 0}%` }}
                            />
                            <span className="progress-text">{enrollment.progress || 0}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-enrollments">No courses enrolled</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManager;